import { ExternalLinkIcon } from 'lucide-react';

import { explorerAddressUrl, truncateAddress } from '@/utils/chains';

/**
 * An on-chain address, truncated and linked to the right explorer.
 *
 * WHY THIS IS A COMPONENT
 * The panel had this pattern inlined in four places, and each copy hard-coded
 * `REACT_APP_BLOCKCHAIN_EXPLORER_URL` — a single global value. That was correct while every
 * token lived on one network and became wrong the moment a second one did: a Polygon token
 * linked to Snowtrace resolves to a page for an address that does not exist there, which
 * reads as "the token is missing" rather than "the link is wrong".
 *
 * Every copy also truncated with `substring(-1, 4)`, which is `substring(0, 4)` — so the
 * "last four characters" were the first four, and every address rendered as 0x123456...0x12.
 *
 * `href` takes the API-provided link when there is one. The backend is the only side that
 * knows which network a given row is on, so its answer wins; the chain-derived URL is the
 * fallback for responses that predate `explorerUrl`.
 */
interface ChainAddressProps {
  address: string | null | undefined;
  /** Chain id, e.g. AVALANCHE_FUJI. Determines both the explorer and the truncation. */
  chain?: string | null;
  /** Explorer link from the API. Preferred over anything derived here. */
  href?: string | null;
  className?: string;
}

export const ChainAddress = ({
  address,
  chain,
  href,
  className,
}: ChainAddressProps) => {
  if (!address) return <span className="text-gray-400">N/A</span>;

  const url =
    href ??
    explorerAddressUrl(chain, address) ??
    // Last resort for a backend that predates explorerUrl and a chain this build does not
    // know. Wrong on a multi-chain deployment, which is why it is last.
    (process.env.REACT_APP_BLOCKCHAIN_EXPLORER_URL
      ? `${process.env.REACT_APP_BLOCKCHAIN_EXPLORER_URL}/address/${address}`
      : null);

  return (
    <span className={`inline-flex items-center gap-2 ${className ?? ''}`}>
      <span title={address} className="font-mono">
        {truncateAddress(chain, address)}
      </span>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <ExternalLinkIcon size={16} className="text-blue-700" />
        </a>
      )}
    </span>
  );
};

export default ChainAddress;
